import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, formatRelative } from "@/lib/api";
import { toast } from "sonner";
import {
  Archive, Play, ShieldCheck, RotateCcw, Trash2, Loader2, RefreshCw,
  CheckCircle2, AlertTriangle, HardDrive, Clock, Save, Lock, Cloud, Database,
} from "lucide-react";
import MetricHelp from "@/components/MetricHelp";

const STATUS_TONE = {
  success: { bg: "bg-[#DCFCE7]", text: "text-[#166534]", label: "Success", Icon: CheckCircle2 },
  running: { bg: "bg-[#DBEAFE]", text: "text-[#1E40AF]", label: "Running", Icon: Loader2 },
  failed: { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", label: "Failed", Icon: AlertTriangle },
  pending: { bg: "bg-[#FEF3C7]", text: "text-[#92400E]", label: "Pending", Icon: Clock },
};

function humanBytes(n) {
  if (!n && n !== 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, val = Number(n);
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(val < 10 && i > 0 ? 2 : 0)} ${units[i]}`;
}

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.pending;
  const Icon = tone.Icon;
  return (
    <span
      data-testid={`backup-status-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${tone.bg} ${tone.text}`}
    >
      <Icon className={`w-3 h-3 ${status === "running" ? "animate-spin" : ""}`} />
      {tone.label}
    </span>
  );
}

function MetricCard({ label, value, sub, tone = "text-slate-900", testId, helpTopic }) {
  return (
    <div
      data-testid={testId}
      className="p-4 rounded-2xl border border-[#E5E5E0] bg-white"
    >
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 flex items-center">
        {label}
        {helpTopic && <MetricHelp topicKey={helpTopic} size="sm" side="bottom" align="start" />}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function ConfigForm({ config, onSave, saving }) {
  const [form, setForm] = useState(config);
  useEffect(() => { setForm(config); }, [config]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form
      data-testid="backup-config-form"
      className="p-5 rounded-2xl border border-[#E5E5E0] bg-white space-y-4"
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}
    >
      <div className="flex items-center gap-2 border-b border-[#E5E5E0] pb-3">
        <Save className="w-5 h-5 text-[#1E3A8A]" />
        <h3 className="text-base font-semibold text-[#1E3A8A]">Backup Configuration</h3>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
            Frequency
          </span>
          <select
            data-testid="backup-config-frequency"
            value={form.frequency || "daily"}
            onChange={(e) => update("frequency", e.target.value)}
            className="w-full border border-[#E5E5E0] rounded-lg px-3 py-2 bg-white"
          >
            <option value="daily">Daily (pending scheduler activation)</option>
            <option value="weekly">Weekly (pending scheduler activation)</option>
            <option value="manual">Manual only</option>
          </select>
          <span className="mt-1 block text-[10px] text-slate-500">
            Automated schedules are pending HarborWell authorization; only
            Manual runs are currently executed.
          </span>
        </label>

        <label className="block">
          <span className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
            Retention (days)
          </span>
          <input
            data-testid="backup-config-retention-days"
            type="number"
            min={1}
            max={3650}
            value={form.retention_days || 30}
            onChange={(e) => update("retention_days", parseInt(e.target.value, 10) || 30)}
            className="w-full border border-[#E5E5E0] rounded-lg px-3 py-2"
          />
        </label>

        <label className="block">
          <span className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
            Offsite target
          </span>
          <select
            data-testid="backup-config-offsite-target"
            value={form.offsite_target || "local"}
            onChange={(e) => update("offsite_target", e.target.value)}
            className="w-full border border-[#E5E5E0] rounded-lg px-3 py-2 bg-white"
          >
            <option value="local">Local disk (preview / dev)</option>
            <option value="s3">Amazon S3 (production)</option>
          </select>
        </label>

        <label className="block md:col-span-2">
          <span className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
            S3 destination
          </span>
          {form.offsite_target === "s3" ? (
            <div
              data-testid="backup-config-s3-destination-locked"
              className="w-full border border-[#E5E5E0] rounded-lg px-3 py-2 bg-[#F9F8F6] text-xs text-slate-600 space-y-1"
            >
              <div>
                <Cloud className="inline w-3 h-3 mr-1 text-[#1E3A8A]" />
                <span className="font-semibold text-slate-700">
                  Managed by HarborWell production Custom Keys
                </span>
              </div>
              <div>
                Bucket, prefix, region, and KMS key are read from the protected
                backend environment (<code className="px-1 bg-white rounded">AWS_S3_BACKUP_BUCKET</code>,{" "}
                <code className="px-1 bg-white rounded">AWS_S3_BACKUP_PREFIX</code>,{" "}
                <code className="px-1 bg-white rounded">AWS_REGION</code>,{" "}
                <code className="px-1 bg-white rounded">AWS_KMS_KEY_ID</code>). These
                fields cannot be redirected from the admin UI.
              </div>
            </div>
          ) : (
            <input
              data-testid="backup-config-s3-bucket"
              type="text"
              value={form.s3_bucket || ""}
              onChange={(e) => update("s3_bucket", e.target.value)}
              placeholder="Not used for Local disk (preview / dev)"
              disabled
              className="w-full border border-[#E5E5E0] rounded-lg px-3 py-2 bg-[#F9F8F6] text-slate-400 cursor-not-allowed"
            />
          )}
        </label>

        <label className="flex items-center gap-2 md:col-span-2 mt-2">
          <input
            data-testid="backup-config-encryption-enabled"
            type="checkbox"
            checked={!!form.encryption_enabled}
            onChange={(e) => update("encryption_enabled", e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm">
            <Lock className="inline w-4 h-4 text-[#1E3A8A] mr-1" />
            Encrypt backup artifacts at rest (Fernet AES128) — requires
            <code className="mx-1 px-1 bg-[#F9F8F6] rounded text-xs">BACKUP_ENCRYPTION_KEY</code> env
          </span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="submit"
          data-testid="backup-config-save"
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#152C6C] disabled:opacity-60 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Configuration
        </button>
      </div>
    </form>
  );
}

function RunActions({ run, onVerify, onDrill, onDelete, busy }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        data-testid={`backup-run-verify-${run.id}`}
        onClick={() => onVerify(run)}
        disabled={busy || run.status !== "success"}
        className="px-2 py-1 text-xs rounded-md border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#F1F0EA] disabled:opacity-40 flex items-center gap-1"
      >
        <ShieldCheck className="w-3 h-3" /> Verify
      </button>
      <button
        data-testid={`backup-run-drill-${run.id}`}
        onClick={() => onDrill(run)}
        disabled={busy || run.status !== "success"}
        className="px-2 py-1 text-xs rounded-md border border-[#166534] text-[#166534] hover:bg-[#DCFCE7] disabled:opacity-40 flex items-center gap-1"
      >
        <RotateCcw className="w-3 h-3" /> Restore Drill
      </button>
      <button
        data-testid={`backup-run-delete-${run.id}`}
        onClick={() => onDelete(run)}
        disabled={busy}
        className="px-2 py-1 text-xs rounded-md border border-[#991B1B] text-[#991B1B] hover:bg-[#FEE2E2] flex items-center gap-1"
      >
        <Trash2 className="w-3 h-3" /> Delete
      </button>
    </div>
  );
}

export default function AdminBackupDisasterRecovery() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const [drillResult, setDrillResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, st, list] = await Promise.all([
        api.get("/admin/backup/config").then((r) => r.data),
        api.get("/admin/backup/compliance-status").then((r) => r.data),
        api.get("/admin/backup/runs").then((r) => r.data),
      ]);
      setConfig(cfg);
      setStatus(st);
      setRuns(list.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      const cfg = await api.put("/admin/backup/config", patch).then((r) => r.data);
      setConfig(cfg);
      toast.success("Backup configuration saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const runBackup = async () => {
    setRunning(true);
    try {
      const doc = await api.post("/admin/backup/runs").then((r) => r.data);
      if (doc.status === "success") {
        toast.success(`Backup complete — ${humanBytes(doc.size_bytes)} across ${(doc.collections_backed_up || []).length} collections.`);
      } else {
        toast.error(`Backup failed: ${doc.error || "unknown error"}`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const verifyRun = async (run) => {
    setBusyRow(run.id);
    try {
      const res = await api.post(`/admin/backup/runs/${run.id}/verify`).then((r) => r.data);
      if (res.ok) {
        toast.success(`Integrity verified — SHA-256 matches (${humanBytes(res.size_bytes)}).`);
      } else {
        toast.error(`Verification failed: ${res.reason || "hash mismatch"}`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyRow(null);
    }
  };

  const drillRun = async (run) => {
    setBusyRow(run.id);
    setDrillResult(null);
    try {
      const res = await api.post(`/admin/backup/runs/${run.id}/restore-drill`).then((r) => r.data);
      setDrillResult({ run_id: run.id, ...res });
      toast.success(`Restore drill OK — ${res.total_docs} docs across ${Object.keys(res.collections).length} collections.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyRow(null);
    }
  };

  const deleteRun = async (run) => {
    if (!window.confirm(`Delete backup run ${run.id.slice(0,8)}? Artifact will be unlinked.`)) return;
    setBusyRow(run.id);
    try {
      await api.delete(`/admin/backup/runs/${run.id}`);
      toast.success("Backup run deleted.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyRow(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-backup-dr">
      {/* Hero */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#1E3A8A]">
            <Archive className="w-3 h-3" /> Enterprise Governance
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold mt-1">
            Backup &amp; Disaster Recovery
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Configure retention, encryption, and offsite targets. Trigger manual
            backups, verify artifact integrity, and run restore drills without
            touching the live database.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="backup-refresh"
            onClick={load}
            disabled={loading}
            className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-slate-700 hover:bg-[#F9F8F6] text-sm flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            data-testid="backup-run-now"
            onClick={runBackup}
            disabled={running}
            className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#152C6C] disabled:opacity-60 flex items-center gap-2"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Run Backup Now
          </button>
        </div>
      </div>

      {/* KPI header */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          testId="backup-kpi-last-success"
          helpTopic="backup_last_success"
          label="Last successful backup"
          value={status?.last_success_at ? formatRelative(status.last_success_at) : "Never"}
          sub={status?.last_success_at ? new Date(status.last_success_at).toLocaleString() : "Run a backup to get started"}
          tone={status?.within_expected_window ? "text-[#166534]" : "text-[#991B1B]"}
        />
        <MetricCard
          testId="backup-kpi-latest-size"
          helpTopic="backup_latest_size"
          label="Latest artifact size"
          value={humanBytes(status?.last_success_size_bytes)}
          sub={status?.last_success_verified_ok === true ? "Integrity verified" :
               status?.last_success_verified_ok === false ? "HASH MISMATCH" :
               "Not yet verified"}
          tone={status?.last_success_verified_ok === false ? "text-[#991B1B]" : "text-slate-900"}
        />
        <MetricCard
          testId="backup-kpi-success-rate"
          helpTopic="backup_success_rate"
          label="Success rate (30d)"
          value={status?.success_rate_30d != null ? `${status.success_rate_30d}%` : "—"}
          sub={`${status?.runs_last_30d ?? 0} runs in the window`}
        />
        <MetricCard
          testId="backup-kpi-configuration"
          helpTopic="backup_configuration"
          label="Configuration"
          value={config?.frequency || "—"}
          sub={`${config?.offsite_target || "local"} · retention ${config?.retention_days || 0}d · ${config?.encryption_enabled ? "encrypted" : "unencrypted"}`}
        />
      </div>

      {/* Configuration form */}
      {config && (
        <ConfigForm config={config} onSave={saveConfig} saving={saving} />
      )}

      {/* Compliance evidence */}
      {status && (
        <div
          data-testid="backup-compliance-evidence"
          className="p-4 rounded-2xl border border-[#E5E5E0] bg-[#F9F8F6]"
        >
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4 text-[#1E3A8A]" />
            <h4 className="text-sm font-semibold text-[#1E3A8A]">Compliance Center evidence</h4>
          </div>
          <div className="grid gap-2 md:grid-cols-3 text-xs">
            <div>
              <span className="text-slate-500 mr-1">Freshness window:</span>
              <strong>{status.within_expected_window ? "Within window" : "Stale / never run"}</strong>
              {status.expected_window_hours && (
                <span className="text-slate-500 ml-1">(expected &lt; {status.expected_window_hours}h)</span>
              )}
            </div>
            <div>
              <span className="text-slate-500 mr-1">Latest SHA-256:</span>
              <code className="text-[10px] break-all">
                {status.last_success_sha256 ? `${status.last_success_sha256.slice(0, 16)}…` : "—"}
              </code>
            </div>
            <div>
              <span className="text-slate-500 mr-1">Offsite:</span>
              <strong>{status.offsite_target === "s3" ? <><Cloud className="inline w-3 h-3" /> S3</> : <><HardDrive className="inline w-3 h-3" /> Local</>}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Restore drill result */}
      {drillResult && (
        <div
          data-testid="backup-drill-result"
          className="p-4 rounded-2xl border border-[#166534] bg-[#DCFCE7]"
        >
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-[#166534] flex items-center gap-2">
              <RotateCcw className="w-4 h-4" /> Restore drill successful
            </h4>
            <button
              data-testid="backup-drill-close"
              onClick={() => setDrillResult(null)}
              className="text-xs text-[#166534] hover:underline"
            >
              Close
            </button>
          </div>
          <div className="text-sm">
            <p><strong>{drillResult.total_docs}</strong> documents across <strong>{Object.keys(drillResult.collections || {}).length}</strong> collections restored to memory.</p>
            <p className="text-xs mt-1 text-slate-600">
              This was a <em>dry-run</em> — no data was written to the live database.
            </p>
          </div>
        </div>
      )}

      {/* Runs history */}
      <div className="p-5 rounded-2xl border border-[#E5E5E0] bg-white" data-testid="backup-runs-panel">
        <div className="flex items-center gap-2 border-b border-[#E5E5E0] pb-3 mb-3">
          <Archive className="w-5 h-5 text-[#1E3A8A]" />
          <h3 className="text-base font-semibold text-[#1E3A8A]">Backup History</h3>
          <span className="text-xs text-slate-500 ml-auto">
            {runs.length} run{runs.length === 1 ? "" : "s"}
          </span>
        </div>

        {loading && !runs.length && (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" />
            Loading backup history...
          </div>
        )}

        {!loading && !runs.length && (
          <div className="p-8 text-center text-slate-500" data-testid="backup-runs-empty">
            <Archive className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <p className="font-medium">No backups yet</p>
            <p className="text-xs mt-1">Click <strong>Run Backup Now</strong> above to create your first backup.</p>
          </div>
        )}

        {runs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="backup-runs-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#E5E5E0]">
                  <th className="py-2 pr-2">Started</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2">Size</th>
                  <th className="py-2 pr-2">Collections</th>
                  <th className="py-2 pr-2">SHA-256</th>
                  <th className="py-2 pr-2">Verified</th>
                  <th className="py-2 pr-2">Trigger</th>
                  <th className="py-2 pr-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F0EA]">
                {runs.map((r) => (
                  <tr key={r.id} data-testid={`backup-run-row-${r.id}`}>
                    <td className="py-2 pr-2 text-xs text-slate-700">
                      <div>{formatRelative(r.started_at)}</div>
                      <div className="text-[10px] text-slate-500">{new Date(r.started_at).toLocaleString()}</div>
                    </td>
                    <td className="py-2 pr-2"><StatusPill status={r.status} /></td>
                    <td className="py-2 pr-2 text-xs">{humanBytes(r.size_bytes)}</td>
                    <td className="py-2 pr-2 text-xs">
                      {(r.collections_backed_up || []).length}
                      {r.doc_counts && (
                        <div className="text-[10px] text-slate-500">
                          {Object.values(r.doc_counts).reduce((a, b) => a + b, 0)} docs
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-2">
                      {r.sha256 ? (
                        <code className="text-[10px]">{r.sha256.slice(0, 12)}…</code>
                      ) : "—"}
                    </td>
                    <td className="py-2 pr-2 text-xs">
                      {r.verified_ok === true ? (
                        <span className="text-[#166534] font-semibold">✓ OK</span>
                      ) : r.verified_ok === false ? (
                        <span className="text-[#991B1B] font-semibold">✗ Mismatch</span>
                      ) : (
                        <span className="text-slate-400">Not verified</span>
                      )}
                    </td>
                    <td className="py-2 pr-2 text-xs">
                      <div>{r.triggered_by}</div>
                      {r.triggered_by_user && (
                        <div className="text-[10px] text-slate-500">{r.triggered_by_user}</div>
                      )}
                    </td>
                    <td className="py-2 pr-2">
                      <RunActions
                        run={r}
                        onVerify={verifyRun}
                        onDrill={drillRun}
                        onDelete={deleteRun}
                        busy={busyRow === r.id}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
